/**
 * The normalized input the builder reads.
 *
 * Deliberately NOT `PoolData` directly: the same market travels through this
 * codebase in two casings — the in-package shape (`totalDepositsUSD`,
 * `variableBorrowRate`) and the API-serialized shape (`totalDepositsUsd`,
 * nested `caps`/`flags`/`config`). The builder must work on both, because it
 * runs in-package during a fetch AND at the worker while proxying an origin
 * response.
 *
 * So: one tolerant reader (`toTermSheetInput`) normalizes either shape into
 * this interface, and the builder itself is pure over the normalized form.
 */

export interface TermConfigEntry {
  category: number | string
  label?: string
  borrowCollateralFactor?: number
  collateralFactor?: number
  borrowFactor?: number
  liquidationPenalty?: number
  closeFactor?: number
  targetHealthFactor?: number
  collateralDisabled?: boolean
  debtDisabled?: boolean
}

export interface TermRewardInput {
  asset?: string
  depositRate?: number
  variableBorrowRate?: number
  stableBorrowRate?: number
  /** Merkl / points programs mark themselves; absent ⇒ a normal token. */
  kind?: string
  endsAt?: number
  claim?: string
}

export interface TermMenuInput {
  termId: number
  durationSecs: number
  durationDays: number
  apr: number
  depositApr?: number
  available?: number
}

/** Normalized market facts the generic builder needs. */
export interface TermSheetInput {
  marketUid: string
  lender: string
  chainId: string
  /** Underlying asset of THIS row. */
  asset?: {
    chainId?: string
    address?: string
    symbol?: string
    name?: string
    decimals?: number
    assetGroup?: string
    logoURI?: string
  }
  underlying?: string
  decimals?: number

  // rates (percent)
  depositRate?: number
  variableBorrowRate?: number
  stableBorrowRate?: number
  intrinsicYield?: number
  rewards?: TermRewardInput[]
  rateModel?: string
  originationFee?: number

  // totals
  totalDeposits?: number
  totalDebt?: number
  totalDebtStable?: number
  totalLiquidity?: number
  borrowLiquidity?: number
  totalDepositsUsd?: number
  totalDebtUsd?: number
  totalLiquidityUsd?: number
  utilization?: number
  irmTotalDeposits?: number
  irmTotalDebt?: number
  lockupRatio?: number

  // caps
  supplyCap?: number
  borrowCap?: number
  debtCeiling?: string | number
  /** Minimum borrow in RAW base units (Compound V3 `baseBorrowMin`). */
  minBorrow?: string

  // flags
  isActive?: boolean
  isFrozen?: boolean
  borrowingEnabled?: boolean
  depositsEnabled?: boolean
  collateralActive?: boolean
  hasStable?: boolean
  variableBorrowDisabled?: boolean

  // risk config, keyed by category
  config?: Record<string, TermConfigEntry>
  closeFactor?: number
  targetHealthFactor?: number

  // fixed-term extras (legacy fields the builder reads as inputs)
  fixedTerm?: {
    model?: string
    maturity?: number
    fees?: {
      continuousFeeApr?: number
      settlementFee?: number
      latePenaltyApr?: number
      originationFeePercent?: number
    }
    earlyRepay?: { kind?: string }
    provider?: { kind?: string; address?: string }
    auction?: Record<string, unknown>
  }
  terms?: TermMenuInput[]

  /** Market-level params (`params.market`) when the lender has them. */
  market?: Record<string, unknown>
}

const num = (v: unknown): number | undefined => {
  if (v == null) return undefined
  const n = typeof v === 'string' ? Number(v) : (v as number)
  return Number.isFinite(n) ? n : undefined
}

/** Read a field under either casing (`fooUSD` / `fooUsd`), plus a nested path. */
function pick(src: Record<string, any>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (k.includes('.')) {
      let cur: any = src
      for (const seg of k.split('.')) {
        if (cur == null) break
        cur = cur[seg]
      }
      if (cur != null) return cur
    } else if (src[k] != null) {
      return src[k]
    }
  }
  return undefined
}

/**
 * Normalize either the in-package `PoolData`-ish row or an API `LendingMarket`
 * item into {@link TermSheetInput}. Tolerant by design: a field missing under
 * one casing is looked up under the other, and nested `caps`/`flags` bundles
 * are unwrapped.
 */
export function toTermSheetInput(
  row: Record<string, any>,
  ctx: {
    marketUid?: string
    lender?: string
    chainId?: string
    market?: Record<string, any>
    /**
     * Item-level fixed-term descriptor. `/lending/latest` attaches `fixedTerm`
     * to the LENDER item, not to each market row, so without this every
     * fixed-term market would silently lose its maturity, its fees and its
     * auction window. A row-level `fixedTerm` (how `/pools/latest` serializes
     * it) is more specific and wins.
     */
    fixedTerm?: Record<string, any>
  } = {},
): TermSheetInput {
  const marketUid =
    ctx.marketUid ?? (pick(row, 'marketUid', 'poolId') as string) ?? ''
  const lender =
    ctx.lender ??
    (pick(row, 'lender', 'lenderKey', 'lenderInfo.key') as string) ??
    marketUid.split(':')[0] ??
    ''
  const chainId =
    ctx.chainId ??
    (pick(row, 'chainId') as string) ??
    marketUid.split(':')[1] ??
    ''

  const rawConfig = (pick(row, 'config') as Record<string, any>) ?? undefined
  const config = rawConfig
    ? Object.fromEntries(
        Object.entries(rawConfig).map(([k, v]) => [
          k,
          {
            category: (v?.category ?? k) as number | string,
            label: v?.label,
            borrowCollateralFactor: num(v?.borrowCollateralFactor),
            collateralFactor: num(v?.collateralFactor),
            borrowFactor: num(v?.borrowFactor),
            liquidationPenalty: num(v?.liquidationPenalty),
            closeFactor: num(v?.closeFactor),
            targetHealthFactor: num(v?.targetHealthFactor),
            collateralDisabled: v?.collateralDisabled === true,
            debtDisabled: v?.debtDisabled === true,
          } satisfies TermConfigEntry,
        ]),
      )
    : undefined

  const rewards = (pick(row, 'rewards') as any[])?.map((r) => ({
    asset: r?.asset,
    depositRate: num(r?.depositRate),
    variableBorrowRate: num(r?.variableBorrowRate),
    stableBorrowRate: num(r?.stableBorrowRate),
    kind: r?.kind,
    endsAt: num(r?.endsAt),
    claim: r?.claim,
  }))

  return {
    marketUid,
    lender,
    chainId,
    asset: pick(
      row,
      'asset',
      'underlyingInfo.asset',
    ) as TermSheetInput['asset'],
    underlying: pick(row, 'underlying') as string,
    decimals: num(pick(row, 'decimals')),

    depositRate: num(pick(row, 'depositRate')),
    variableBorrowRate: num(pick(row, 'variableBorrowRate')),
    stableBorrowRate: num(pick(row, 'stableBorrowRate')),
    intrinsicYield: num(pick(row, 'intrinsicYield')),
    rewards,
    rateModel: pick(row, 'rateModel') as string,
    originationFee: num(pick(row, 'originationFee')),

    totalDeposits: num(pick(row, 'totalDeposits')),
    totalDebt: num(pick(row, 'totalDebt')),
    totalDebtStable: num(pick(row, 'totalDebtStable')),
    totalLiquidity: num(pick(row, 'totalLiquidity')),
    borrowLiquidity: num(pick(row, 'borrowLiquidity')),
    totalDepositsUsd: num(pick(row, 'totalDepositsUSD', 'totalDepositsUsd')),
    totalDebtUsd: num(pick(row, 'totalDebtUSD', 'totalDebtUsd')),
    totalLiquidityUsd: num(pick(row, 'totalLiquidityUSD', 'totalLiquidityUsd')),
    utilization: num(pick(row, 'utilization')),
    irmTotalDeposits: num(pick(row, 'irmTotalDeposits')),
    irmTotalDebt: num(pick(row, 'irmTotalDebt')),
    lockupRatio: num(pick(row, 'lockupRatio')),

    minBorrow:
      (pick(row, 'minBorrow', 'caps.minBorrow') as string) ?? undefined,
    supplyCap: num(pick(row, 'supplyCap', 'caps.supplyCap')),
    borrowCap: num(pick(row, 'borrowCap', 'caps.borrowCap')),
    debtCeiling: pick(row, 'debtCeiling', 'caps.debtCeiling') as string,

    isActive: pick(row, 'isActive', 'flags.isActive') as boolean,
    isFrozen: pick(row, 'isFrozen', 'flags.isFrozen') as boolean,
    borrowingEnabled: pick(
      row,
      'borrowingEnabled',
      'flags.borrowingEnabled',
    ) as boolean,
    depositsEnabled: pick(
      row,
      'depositsEnabled',
      'flags.depositsEnabled',
    ) as boolean,
    collateralActive: pick(
      row,
      'collateralActive',
      'flags.collateralActive',
    ) as boolean,
    hasStable: pick(row, 'hasStable', 'flags.hasStable') as boolean,
    variableBorrowDisabled: pick(
      row,
      'variableBorrowDisabled',
      'flags.variableBorrowDisabled',
    ) as boolean,

    config,
    // `caps.` is NOT optional here. `/pools/latest` serializes both of these as
    // TOP-LEVEL row fields, while `/lending/latest` — the endpoint the lending
    // term sheets are actually stamped on — nests them under `caps` alongside
    // the supply/borrow caps (yield-tracer `utils/lending.ts`). Reading only
    // the flat key made every sheet on that endpoint fall through to the
    // builder's `?? 1`, so Aave and Compound V2 published "the whole debt can
    // be repaid in one liquidation" against a real close factor of 0.5, and
    // Aave V4's `targetHealthFactor` vanished entirely.
    closeFactor: num(pick(row, 'closeFactor', 'caps.closeFactor')),
    targetHealthFactor: num(
      pick(row, 'targetHealthFactor', 'caps.targetHealthFactor'),
    ),

    fixedTerm: ((pick(row, 'fixedTerm') as TermSheetInput['fixedTerm']) ??
      ctx.fixedTerm) as TermSheetInput['fixedTerm'],
    terms: pick(row, 'terms') as TermMenuInput[],
    market: resolveMarketDescriptors(row, ctx.market),
  }
}

/**
 * Per-lender descriptors (`liquity`, `river`, `teller`, `inverse`, …) reach us
 * two different ways: under `params.market.<key>` on the in-package shape, and
 * as TOP-LEVEL row fields on `/pools/latest`. Merge both so an adapter reads
 * one place regardless of which endpoint produced the row.
 */
const ROW_DESCRIPTOR_KEYS = [
  'liquity',
  'river',
  'teller',
  'inverse',
  // Cooler: the price-LTV ratchet, the drip schedule and the minDebt gate —
  // the descriptor is the only carrier of the protocol truth (the fractional
  // factors on the row are display-only).
  'cooler',
  'exactly',
  'usdd',
  'dss',
  'termmax',
  // These five were missing, and the failure is SILENT: the adapter still
  // runs, reads an empty descriptor and returns a sheet that looks complete
  // while omitting the lender's whole reason for having an adapter. Any lender
  // with an entry in `TERM_ADAPTERS` that reads `input.market.<key>` belongs
  // here — the adapter list and this list must not drift apart.
  'llamalend',
  'frankencoin',
  'resupply',
  'gearbox',
  'curvance',
  // Fluid: distinguishes a SMART (T2/T3/T4) vault, whose collateral and/or
  // debt is a two-token DEX LP position, from an ordinary T1 pool row.
  'fluid',
  // Aave V4: the liquidation BONUS CURVE (`healthFactorForMaxBonus`,
  // `liquidationBonusFactor`). The sizing knob `targetHealthFactor` is a
  // market-level column and arrives without this; the curve is what turns the
  // published max bonus from a flat penalty into a bound.
  'aaveV4',
  'broker',
  'collateralProvider',
  'loanProvider',
  // Morpho Blue's protocol fee — the ONE parameter of an otherwise immutable
  // market that governance can move. Nested under `params.market` on the
  // in-package shape but a top-level field on `/pools/latest`, so without it
  // here the flat path reports a confident 0 % on every Blue market.
  'fee',
] as const

function resolveMarketDescriptors(
  row: Record<string, any>,
  ctxMarket: Record<string, any> | undefined,
): Record<string, any> | undefined {
  const fromParams = pick(row, 'params.market') as
    | Record<string, any>
    | undefined
  const rowLevel: Record<string, any> = {}
  for (const k of ROW_DESCRIPTOR_KEYS) {
    if (row[k] != null) rowLevel[k] = row[k]
  }
  const merged = { ...(ctxMarket ?? {}), ...(fromParams ?? {}), ...rowLevel }
  return Object.keys(merged).length > 0 ? merged : undefined
}
